import { Component, ElementRef, Input, ViewChild, computed, signal } from '@angular/core';
import { Theme, VideoWidget, VideoLayout } from '../../models/reader-document';

@Component({
  selector: 'wr-video-widget',
  standalone: true,
  template: `
    <div [style.background]="background()" [class.px-3]="layout() === 'standard'">
      <!-- Title/Subtitles -->
      @if (widget()?.title || widget()?.subtitle && layout() !== 'billboard' && layout() !== 'splash') {
        <div class="px-1 mb-2" [style.text-align]="textAlign()">
          @if (widget()?.title) {
            <div class="title" [style.color]="titleColor()" [style.font-family]="titleFont()">
              {{ widget()?.title }}
            </div>
          }
          @if (widget()?.subtitle) {
            <div class="subtitle" [style.color]="subtitleColor()" [style.font-family]="subtitleFont()">
              {{ widget()?.subtitle }}
            </div>
          }
        </div>
      }

      <div style="position: relative; display: inline-block; width: 100%;">
        <video
          #videoEl
          [attr.aria-label]="altText()"
          playsinline
          [attr.webkit-playsinline]="''"
          [attr.controls]="showNativeControls() ? '' : null"
          [attr.autoplay]="autoPlay() ? '' : null"
          [attr.muted]="mutedAttr() ? '' : null"
          [attr.loop]="loopAttr() ? '' : null"
          [poster]="poster() || null"
          [style.width]="'100%'"
          [style.height]="videoHeight()"
          [style.object-fit]="objectFit()"
          [style.border-radius.px]="videoCornerRadius()"
          (play)="playing.set(true)"
          [attr.preload]="preloadAttr()"
          (pause)="playing.set(false)"
          (loadeddata)="tryAutoplayOnce()"
          (click)="handleVideoClick()"
        >
          <source [attr.src]="src()" [attr.type]="videoMimeType()" />
        </video>

        <!-- Custom overlay controls (only when no native controls AND video is paused) -->
        @if (showOverlayControls() && !playing()) {
          <div
            style="position: absolute; inset: 0; display: flex; justify-content: center; align-items: center;"
            (click)="togglePlay()"
          >
            <button
              type="button"
              (click)="$event.stopPropagation(); togglePlay()"
              [attr.aria-label]="playing() ? 'Pause video' : 'Play video'"
              style="background: rgba(0,0,0,0.45); border: none; border-radius: 9999px; padding: 10px; cursor: pointer;"
            >
              @if (!playing()) {
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                  <path d="M8 5v14l11-7z"></path>
                </svg>
              } @else {
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                  <path d="M6 5h4v14H6zM14 5h4v14h-4z"></path>
                </svg>
              }
            </button>
          </div>
        }

        <!-- Splash wipe-up indicator -->
        @if (layout() === 'splash' && widget()?.showMoreIndicator) {
          <div class="wr-swipe-indicator">
            <img
              src="assets/icons/swipe-up.svg"
              alt="Swipe up"
              width="30"
              height="30"
            />
          </div>
        }

      </div>
    </div>
  `,
})
export class VideoWidgetComponent {
  private widgetSig = signal<VideoWidget | null>(null);
  private themeSig = signal<Theme & { isDark?: boolean } | null>(null);

  @ViewChild('videoEl') videoEl?: ElementRef<HTMLVideoElement>;

  playing = signal(false);

  @Input() set json(value: VideoWidget) {
    this.widgetSig.set(value);
  }

  @Input() set theme(value: Theme | undefined) {
    this.themeSig.set(value ?? null);
  }

  widget = computed(() => this.widgetSig());

  private colors = computed(() => {
    const t = this.themeSig();
    if (!t) return null;
    return t.isDark ? t.darkColors : t.lightColors;
  });

  // layout
  
  layout = computed<VideoLayout>(() => {
    const w = this.widget();
    if (!w) return 'standard';

    // legacy types
    if (w.type === 'billboardVideo') return 'billboard';
    if (w.type === 'splashVideo') return 'splash';

    return w.layout ?? 'standard';
  });

  // styles

  background = computed(() => this.widget()?.style?.['background'] ?? 'transparent');

  textAlign = computed(() => {
    const raw = (this.widget()?.style?.['textAlignment'] as string | undefined) ?? 'left';
    if (raw === 'left' || raw === 'right' || raw === 'center' || raw === 'justify') return raw;
    return 'left';
  });

  titleFont = computed(() => this.themeSig()?.fonts?.title?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  subtitleFont = computed(() => this.themeSig()?.fonts?.subtitle?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');

  titleColor = computed(() => this.widget()?.style?.['titleColor'] ?? this.colors()?.title ?? this.colors()?.primary ?? '#111827');
  subtitleColor = computed(() => this.widget()?.style?.['subtitleColor'] ?? this.colors()?.subtitle ?? this.colors()?.secondary ?? '#4b5563');

  videoCornerRadius = computed(() => {
    const base = this.themeSig()?.cornerRadius ?? 0;
    return this.layout() === 'standard' ? base : 0; // billboard/splash = edge to edge
  });

  // attributes

  src = computed(() => this.widget()?.url ?? '');
  poster = computed(() => this.widget()?.coverImageURL ?? null);

  altText = computed(() => this.widget()?.altTitle ?? this.widget()?.id ?? 'video');

  autoPlay = computed(() => !!this.widget()?.autoPlay);
  loopAttr = computed(() => !!this.widget()?.autoRePlay);

  // Match old behavior:
  // - billboard: autoplay -> muted required
  // - otherwise: muted flag respected; if autoplay true, allow muted too
  mutedAttr = computed(() => {
    const w = this.widget();
    if (!w) return false;
    if (w.autoPlay) return true;
    return !!w.muted;
  });

  showNativeControls = computed(() => !!this.widget()?.videoControls);

  showOverlayControls = computed(() => {
    const w = this.widget();
    if (!w) return false;
    // Show overlay for any video without native controls
    return !w.videoControls;
  });


  videoMimeType = computed(() => {
    const url = this.src();
    if (!url) return 'video/mp4';
    const lower = url.toLowerCase();
    if (lower.endsWith('.mp4')) return 'video/mp4';
    if (lower.endsWith('.mov')) return 'video/mp4';
    return 'video/mp4';
  });


  preloadAttr = computed(() => {
    return 'metadata';
  });

  tryAutoplayOnce() {
    const el = this.videoEl?.nativeElement;
    const w = this.widget();
    if (!el || !w?.autoPlay) return;

    el.muted = true;
    el.playsInline = true;

    const playPromise = el.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.catch((err: any) => {
        console.warn('Autoplay was blocked by the browser:', err);
      });
    }
  }

  handleVideoClick() {
    if (this.showNativeControls()) return;
    this.togglePlay();
  }

  // layout-dependent sizing
  videoHeight = computed(() => (this.layout() === 'splash' ? '100vh' : '100%')); //TODO: reduce vh?
  objectFit = computed(() => (this.layout() === 'splash' ? 'cover' : 'contain'));

  // actions
  togglePlay() {
    const el = this.videoEl?.nativeElement;
    if (!el) return;

    if (el.paused) el.play();
    else el.pause();
  }
}